<script lang="ts">
  import { onDestroy, onMount } from 'svelte';
  import { push } from 'svelte-spa-router';
  import { PoseRunnerProxy } from '../lib/poseRunnerProxy';
  import { analyzeSession } from '../lib/swingAnalysis';
  import { db, newSessionId } from '../lib/db';
  import MiniQuadra from '../lib/court/MiniQuadra.svelte';
  import RallyEndToast from '../lib/match/RallyEndToast.svelte';
  import type { CourtImpact, LivePlayers, LiveBall } from '../lib/court/types';
  import { LiveAnalysisLoop } from '../lib/analysis/liveAnalysisLoop';
  import {
    getSettings,
    initSettings,
    snapshotSettings,
  } from '../lib/settings/settings.svelte';
  import type { Rally } from '@tbc/core/score';

  type TestStatus = 'loading-model' | 'idle' | 'processing' | 'done' | 'error';

  let videoEl = $state<HTMLVideoElement | null>(null);
  let canvasEl = $state<HTMLCanvasElement | null>(null);
  let proxy: PoseRunnerProxy | null = null;
  let loop: LiveAnalysisLoop | null = null;
  let status = $state<TestStatus>('loading-model');
  let errorMsg = $state<string | null>(null);
  let progressPct = $state(0);
  let fileUrl = $state<string | null>(null);
  let videoBlob: Blob | null = null;

  // Reactive mirrors of the loop state.
  let liveImpacts = $state<readonly CourtImpact[]>([]);
  let lastEvent = $state<CourtImpact | null>(null);
  let livePlayers = $state<LivePlayers>({});
  let liveBall = $state<LiveBall | null>(null);
  let calibCorners = $state<readonly [number, number][] | null>(null);
  let calibState = $state<'pending' | 'converged' | 'failed'>('pending');
  let capturedCount = $state(0);
  let cutCount = $state(0);

  let rallyPending = $state<Rally | null>(null);

  const settings = $derived(getSettings());

  onMount(async () => {
    await initSettings();
    try {
      proxy = new PoseRunnerProxy();
      await proxy.whenReady();
      status = 'idle';
    } catch (err) {
      errorMsg = err instanceof Error ? err.message : String(err);
      status = 'error';
    }
  });

  onDestroy(() => {
    loop?.dispose();
    proxy?.dispose();
    if (fileUrl) URL.revokeObjectURL(fileUrl);
  });

  function loadBlob(blob: Blob) {
    videoBlob = blob;
    if (fileUrl) URL.revokeObjectURL(fileUrl);
    fileUrl = URL.createObjectURL(blob);
    const s = status as TestStatus;
    if (s === 'done' || s === 'processing') status = 'idle';
    resetMirrors();
    loop?.reset();
  }

  function resetMirrors() {
    liveImpacts = [];
    lastEvent = null;
    livePlayers = {};
    liveBall = null;
    calibCorners = null;
    calibState = 'pending';
    capturedCount = 0;
    cutCount = 0;
    progressPct = 0;
  }

  function onFileChange(e: Event) {
    const input = e.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    loadBlob(file);
  }

  async function loadDemo(path: string) {
    const s = status as TestStatus;
    if (s === 'loading-model' || s === 'processing') return;
    try {
      const res = await fetch(path);
      if (!res.ok) throw new Error(`Falha ao carregar ${path} (${res.status})`);
      const blob = await res.blob();
      loadBlob(blob);
    } catch (err) {
      errorMsg = err instanceof Error ? err.message : String(err);
      status = 'error';
    }
  }

  async function start() {
    if (!videoEl || !fileUrl || !proxy) return;
    status = 'processing';
    videoEl.muted = true;
    videoEl.playbackRate = 1;
    try {
      await videoEl.play();
    } catch (err) {
      errorMsg = err instanceof Error ? err.message : String(err);
      status = 'error';
      return;
    }
    loop = new LiveAnalysisLoop(
      { video: videoEl, canvas: canvasEl, proxy },
      {
        settings: snapshotSettings(),
        onFrame: (st) => {
          liveImpacts = st.liveImpacts;
          lastEvent = st.lastEvent;
          livePlayers = st.livePlayers;
          liveBall = st.liveBall;
          calibCorners = st.calibCorners;
          calibState = st.calibState;
          capturedCount = st.capturedLength;
          cutCount = st.cutCount;
          if (videoEl) {
            progressPct =
              videoEl.duration > 0 ? (videoEl.currentTime / videoEl.duration) * 100 : 0;
            if (videoEl.ended) void finish();
          }
        },
        onRallyEnd: (rally) => {
          rallyPending = rally;
        },
      },
    );
    loop.start();
  }

  $effect(() => {
    loop?.applySettings(settings);
  });

  async function finish() {
    if (!videoBlob || !videoEl || !loop) return;
    loop.stop();
    const durationSec = videoEl.duration || 1;
    const captured = loop.capturedFrames;
    const estimatedFps = captured.length > 2 ? captured.length / durationSec : 30;

    const id = newSessionId();
    await db.sessions.add({
      id,
      createdAtMs: Date.now(),
      durationMs: durationSec * 1000,
      fps: estimatedFps,
      videoBlob,
      poseSequence: {
        frames: captured.slice(),
        fps: estimatedFps,
        startTimeMs: Date.now(),
      },
      courtTimeline: loop.courtTimeline.slice(),
      ...analyzeSession(captured, estimatedFps, settings.handedness),
    });
    status = 'done';
    push(`/swings/${id}`);
  }

  function onRallyPick(_: 'p1' | 'p2') {
    rallyPending = null;
  }
  function onRallyDismiss() {
    rallyPending = null;
  }
</script>

<section class="flex h-full flex-col gap-4 p-4">
  <header class="flex items-center justify-between">
    <a href="#/" class="rounded-md bg-slate-800 px-3 py-1 active:bg-slate-700">‹ Voltar</a>
    <h1 class="text-xl font-semibold">Testar com vídeo</h1>
    <a
      href="#/settings"
      class="rounded-md bg-slate-800 px-3 py-1 active:bg-slate-700"
      aria-label="Configurações"
      title="Configurações"
    >⚙️</a>
  </header>

  {#if status === 'loading-model'}
    <p class="rounded-md bg-slate-800 p-3 text-slate-300">Carregando modelo de pose…</p>
  {/if}

  {#if status === 'error'}
    <p class="rounded-md bg-red-900/50 p-3">Erro: {errorMsg}</p>
  {/if}

  <div class="flex flex-col gap-2 sm:flex-row sm:items-center">
    <button
      type="button"
      class="rounded-md bg-racket px-4 py-2 text-sm font-semibold text-slate-900 active:scale-95 disabled:opacity-40"
      onclick={() => loadDemo('./demo-video.mp4')}
      disabled={status === 'loading-model' || status === 'processing'}
      title="Clip curto (Federer, 3 min)"
    >
      Demo 1 — clip curto
    </button>
    <button
      type="button"
      class="rounded-md bg-court px-4 py-2 text-sm font-semibold text-slate-900 active:scale-95 disabled:opacity-40"
      onclick={() => loadDemo('./demo-match-10min.mp4')}
      disabled={status === 'loading-model' || status === 'processing'}
      title="10 min da final Open 2ª classe (Gustavo Naves x Pedro Augusto)"
    >
      Demo 2 — partida 10 min
    </button>
    <button
      type="button"
      class="rounded-md bg-sky-500 px-4 py-2 text-sm font-semibold text-slate-900 active:scale-95 disabled:opacity-40"
      onclick={() => loadDemo('./demo-djoko-alcaraz-720p.mp4')}
      disabled={status === 'loading-model' || status === 'processing'}
      title="1 min em 720p — Djokovic x Alcaraz, AO 2025 Quarterfinal"
    >
      Demo 3 — ATP 720p 1 min
    </button>
    <span class="text-sm text-slate-500">ou</span>
    <label class="flex-1 cursor-pointer">
      <input
        type="file"
        accept="video/mp4,video/webm"
        onchange={onFileChange}
        disabled={status === 'loading-model' || status === 'processing'}
        class="w-full rounded-md bg-slate-800 p-2 file:mr-3 file:rounded file:border-0 file:bg-slate-700 file:px-3 file:py-1 file:text-slate-100"
      />
    </label>
  </div>

  {#if fileUrl}
    <div class="grid gap-4 md:grid-cols-2">
      <div class="relative w-full overflow-hidden rounded-md bg-black">
        <!-- svelte-ignore a11y_media_has_caption -->
        <video
          bind:this={videoEl}
          src={fileUrl}
          class="block w-full"
          controls
          playsinline
          muted
        ></video>
        <canvas
          bind:this={canvasEl}
          class="pointer-events-none absolute inset-0 h-full w-full"
        ></canvas>
      </div>

      {#if settings.overlays.miniCourt}
        <div class="min-h-[360px]">
          <MiniQuadra
            impacts={liveImpacts}
            {lastEvent}
            {livePlayers}
            {liveBall}
            showTrail={settings.overlays.ballTrail}
            calibrationBadge={calibState === 'failed'
              ? 'Calibração manual em breve'
              : calibState === 'pending'
                ? 'Calibração em andamento…'
                : null}
          />
        </div>
      {/if}
    </div>

    <div class="flex items-center gap-3">
      {#if status === 'idle'}
        <button
          type="button"
          class="rounded-md bg-court px-6 py-3 font-semibold text-slate-900 active:scale-95"
          onclick={start}
        >
          Analisar vídeo
        </button>
      {:else if status === 'processing'}
        <div class="flex-1">
          <div class="h-2 w-full rounded bg-slate-800">
            <div class="h-full rounded bg-court" style="width: {progressPct}%"></div>
          </div>
          <p class="pt-1 text-sm text-slate-400">
            Processando… {progressPct.toFixed(0)}% — {capturedCount} frames · {liveImpacts.length}
            swings · {cutCount} cortes
          </p>
        </div>
      {:else if status === 'done'}
        <p class="text-slate-300">✓ Concluído. Redirecionando para o relatório…</p>
      {/if}
    </div>
  {/if}

  {#if rallyPending}
    <RallyEndToast onPick={onRallyPick} onDismiss={onRallyDismiss} />
  {/if}

  <p class="pt-2 text-xs text-slate-500">
    Dica: vídeos longos podem demorar. O pipeline roda em tempo real (1× da velocidade do vídeo),
    então 1 minuto de vídeo leva ≈1 minuto processando. A mini-quadra ao lado atualiza a cada
    swing detectado.
  </p>
</section>
